import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountContext } from '../useContext/Account';
import { useMarco, RUTA_INICIO } from './MarcoContext';
import Icono, { iconoCarpeta, nombreCarpeta, sinTildes } from './Icono';

/* «Ir a…» (Ctrl+K): escribir parte del nombre de cualquier pantalla del menú (o de su
   carpeta) y Enter. Sin escribir nada muestra favoritos y recientes.
     ↑ ↓ elegir · Enter abrir · Ctrl+Enter abrir en otra pestaña · Ctrl+D favorito · Esc cerrar */

const puntaje = (texto, consulta) => {
  const t = sinTildes(texto);
  const partes = sinTildes(consulta).split(/\s+/).filter(Boolean);
  if (!partes.length) return 1;
  if (!partes.every((p) => t.includes(p))) return 0;
  let s = 1;
  if (t.startsWith(partes[0])) s += 3;
  if (t.split(/\s+/).some((w) => w.startsWith(partes[0]))) s += 2;
  return s;
};

export default function PaletaComandos() {
  const navigate = useNavigate();
  const { logout } = useContext(AccountContext);
  const {
    paletaAbierta, cerrarPaleta, opciones, opcionesFavoritas, opcionesRecientes,
    esFavorito, alternarFavorito, alternarMenu,
  } = useMarco();
  const [consulta, setConsulta] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listaRef = useRef(null);
  const focoPrevio = useRef(null);

  useEffect(() => {
    if (paletaAbierta) {
      focoPrevio.current = document.activeElement;
      setConsulta('');
      setSel(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [paletaAbierta]);

  const acciones = useMemo(() => [
    { tipo: 'accion', nombre: 'Inicio', detalle: 'Alt+0', icono: 'inicio', hacer: () => navigate(RUTA_INICIO) },
    { tipo: 'accion', nombre: 'Atajos de teclado', detalle: '?', icono: 'teclado', hacer: () => window.dispatchEvent(new Event('sym:ayuda-teclado')) },
    { tipo: 'accion', nombre: 'Contraer / expandir el menú', detalle: 'Ctrl+B', icono: 'contraer', hacer: alternarMenu },
    { tipo: 'accion', nombre: 'Buscar en el menú lateral', detalle: 'Alt+M', icono: 'menu', hacer: () => window.dispatchEvent(new Event('sym:enfocar-menu')) },
    { tipo: 'accion', nombre: 'Cerrar sesión', detalle: '', icono: 'salir', hacer: () => { if (window.confirm('¿Cerrar la sesión?')) logout(); } },
  ], [navigate, alternarMenu, logout]);

  const grupos = useMemo(() => {
    const q = consulta.trim();
    const pantalla = (o, extra) => ({ tipo: 'pantalla', ...o, ...extra });
    if (!q) {
      const out = [];
      if (opcionesFavoritas.length) out.push({ titulo: 'Favoritos', items: opcionesFavoritas.map((o, i) => pantalla(o, { detalle: `Alt+${i + 1}` })) });
      const rec = opcionesRecientes.filter((o) => !opcionesFavoritas.some((f) => f.ruta === o.ruta)).slice(0, 6);
      if (rec.length) out.push({ titulo: 'Recientes', items: rec.map((o) => pantalla(o)) });
      if (!out.length) out.push({ titulo: 'Pantallas', items: opciones.slice(0, 8).map((o) => pantalla(o)) });
      out.push({ titulo: 'Acciones', items: acciones });
      return out;
    }
    const pantallas = opciones
      .map((o) => ({ o, s: Math.max(puntaje(o.nombre, q) * 2, puntaje(`${o.carpeta} ${nombreCarpeta(o.carpeta)} ${o.nombre}`, q)) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.o.nombre.localeCompare(b.o.nombre))
      .slice(0, 30)
      .map((x) => pantalla(x.o));
    const accs = acciones.filter((a) => puntaje(a.nombre, q) > 0);
    const out = [];
    if (pantallas.length) out.push({ titulo: 'Pantallas', items: pantallas });
    if (accs.length) out.push({ titulo: 'Acciones', items: accs });
    return out;
  }, [consulta, opciones, opcionesFavoritas, opcionesRecientes, acciones]);

  const planos = useMemo(() => grupos.flatMap((g) => g.items), [grupos]);

  useEffect(() => { setSel(0); }, [consulta]);
  useEffect(() => {
    const el = listaRef.current && listaRef.current.querySelector(`[data-indice="${sel}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!paletaAbierta) return null;

  const cerrar = (devolverFoco = true) => {
    cerrarPaleta();
    if (devolverFoco && focoPrevio.current && document.contains(focoPrevio.current)) {
      try { focoPrevio.current.focus(); } catch (e) { /* nada */ }
    }
  };

  const ejecutar = (item, nuevaPestana = false) => {
    if (!item) return;
    if (item.tipo === 'pantalla') {
      if (nuevaPestana) { window.open(item.ruta, '_blank', 'noopener'); cerrar(); return; }
      cerrar(false);
      navigate(item.ruta);
      setTimeout(() => { const c = document.querySelector('.sym-contenido'); if (c) c.focus({ preventScroll: true }); }, 50);
    } else {
      cerrar(false);
      item.hacer();
    }
  };

  const alTeclear = (e) => {
    const n = planos.length;
    if (e.key === 'ArrowDown') { setSel((s) => (n ? (s + 1) % n : 0)); }
    else if (e.key === 'ArrowUp') { setSel((s) => (n ? (s - 1 + n) % n : 0)); }
    else if (e.key === 'PageDown') { setSel((s) => Math.min(n - 1, s + 8)); }
    else if (e.key === 'PageUp') { setSel((s) => Math.max(0, s - 8)); }
    else if (e.key === 'Enter') { ejecutar(planos[sel], e.ctrlKey || e.metaKey); }
    else if (e.key === 'Escape') { cerrar(); }
    else if (e.key === 'Tab') { setSel((s) => (n ? (s + (e.shiftKey ? -1 + n : 1)) % n : 0)); }
    else if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
      const it = planos[sel];
      if (it && it.tipo === 'pantalla') alternarFavorito(it.ruta);
    } else if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) { cerrar(); }
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  let indice = -1;
  return (
    <div className="sym-paleta" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrar(); }}>
      <div className="sym-paleta__caja" role="dialog" aria-modal="true" aria-label="Ir a una pantalla o acción">
        <div className="sym-paleta__buscar">
          <Icono nombre="buscar" tam={19} />
          <input
            ref={inputRef}
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            onKeyDown={alTeclear}
            placeholder="Escriba el nombre de una pantalla: resultados, factura, paciente…"
            aria-label="Buscar pantalla o acción"
            aria-controls="sym-paleta-lista"
            aria-activedescendant={planos[sel] ? `sym-paleta-op-${sel}` : undefined}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="sym-kbd">Esc</kbd>
        </div>

        <div className="sym-paleta__lista" id="sym-paleta-lista" role="listbox" ref={listaRef}>
          {grupos.map((g) => (
            <div key={g.titulo} className="sym-paleta__grupo">
              <div className="sym-paleta__titulo">{g.titulo}</div>
              {g.items.map((it) => {
                indice += 1;
                const i = indice;
                const fav = it.tipo === 'pantalla' && esFavorito(it.ruta);
                return (
                  <div
                    key={`${it.tipo}-${it.ruta || it.nombre}`}
                    id={`sym-paleta-op-${i}`}
                    data-indice={i}
                    role="option"
                    aria-selected={i === sel}
                    className={`sym-paleta__op${i === sel ? ' sel' : ''}`}
                    onMouseMove={() => { if (sel !== i) setSel(i); }}
                    onClick={(e) => ejecutar(it, e.ctrlKey || e.metaKey)}
                  >
                    <span className="sym-paleta__ico">
                      <Icono nombre={it.tipo === 'pantalla' ? iconoCarpeta(it.carpeta) : it.icono} tam={17} />
                    </span>
                    <span className="sym-paleta__texto">
                      <strong>{it.nombre}</strong>
                      {it.tipo === 'pantalla' && <small>{nombreCarpeta(it.carpeta)}</small>}
                    </span>
                    {fav && <span className="sym-paleta__estrella" title="Favorito"><Icono nombre="estrella" tam={13} /></span>}
                    {it.detalle && <kbd className="sym-kbd">{it.detalle.replace('+', ' ')}</kbd>}
                    {i === sel && <span className="sym-paleta__enter"><Icono nombre="enter" tam={15} /></span>}
                  </div>
                );
              })}
            </div>
          ))}
          {planos.length === 0 && (
            <div className="sym-paleta__vacio">
              {opciones.length === 0 ? 'El menú todavía se está cargando…' : <>Ninguna pantalla coincide con «{consulta}».</>}
            </div>
          )}
        </div>

        <div className="sym-paleta__pie">
          <span><kbd className="sym-kbd">↑</kbd><kbd className="sym-kbd">↓</kbd> elegir</span>
          <span><kbd className="sym-kbd">Enter</kbd> abrir</span>
          <span><kbd className="sym-kbd">Ctrl Enter</kbd> otra pestaña</span>
          <span><kbd className="sym-kbd">Ctrl D</kbd> favorito</span>
        </div>
      </div>
    </div>
  );
}
